import { useCallback, useLayoutEffect, type CSSProperties } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { AddressSearchBar } from '@/components/map/AddressSearchBar';
import { OnboardingSettingsBar } from '@/components/layout/OnboardingSettingsBar';
import { SlidePanel } from '@/components/ui/SlidePanel';
import { FormFooter } from '@/components/common/form';
import { useGeoMarkupStore } from '@/store';
import { useDeviceLayout } from '@/hooks';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import { MapConfigForm } from './parts/MapConfigForm';
import { MapConfigPreview } from './parts/MapConfigPreview';
import { useMapConfigPage } from './hooks/useMapConfigPage';
import { useMapConfigOverlays } from './hooks/useMapConfigOverlays';
import { useMapConfigCameraPadding } from './hooks/useMapConfigCameraPadding';
import styles from './WorkspaceMapConfigPage.module.css';

/** No-op close handler for first-run mode, where the panel cannot be dismissed. */
const NOOP = (): void => {};

/**
 * Dedicated full-page per-workspace map configurator, rendered outside the
 * AppShell (minimal onboarding-style chrome). Reached on first run via the
 * AppShell gate, or from workspace settings with `?mode=edit`. Layout mirrors
 * the main map: a full-bleed live preview with floating overlays and a right
 * SlidePanel holding the configuration form.
 */
export function WorkspaceMapConfigPage() {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();
  const {
    mode,
    isEdit,
    form,
    canSave,
    isSaving,
    handleSave,
    handleBack,
    redirectTo,
  } = useMapConfigPage();
  const { values, setCenter } = form;
  const isFirstRun = mode === 'firstRun';
  // Mobile preview + overlay-hiding behaviour, mirroring the main map.
  const {
    previewMode,
    setPreviewMode,
    showOverlays,
    bottomOverlayOffset,
    mapBottomInset,
  } = useMapConfigOverlays(isMobile);
  // Camera padding frames the center pin in the visible map area (left of the
  // panel / above the mobile sheet); kept in the orchestrator so the memoized
  // preview stays presentational and isn't subscribed to panel state.
  const cameraPadding = useMapConfigCameraPadding(mapBottomInset);
  // Lift the map-area overlays (zoom pill + drag hint) above the mobile panel
  // in preview / minimized states; the variable cascades from `.page`
  // (an undefined value is omitted by React, so the CSS fallback applies).
  const pageStyle = {
    '--toolbar-bottom-offset': bottomOverlayOffset,
  } as CSSProperties;

  // Address pick recenters the map; the preview's effect flies to it + moves
  // the marker. AddressSearchBar passes (lat, lng) in that order.
  const handleSelectLocation = useCallback(
    (lat: number, lng: number) => setCenter({ lat, lng }),
    [setCenter]
  );

  // Always-open panel: SlidePanel applies `defaultFullscreen` only on a
  // false→true open transition (never happens here), so the global fullscreen
  // flag (initially `true`) would render this fullscreen. Force side-panel mode
  // on mount; useLayoutEffect writes the store before paint (no flash).
  const setSlidePanelFullscreen = useGeoMarkupStore(
    (state) => state.ui.setSlidePanelFullscreen
  );
  useLayoutEffect(() => {
    setSlidePanelFullscreen(false);
  }, [setSlidePanelFullscreen]);

  // Bounce already-configured workspaces away before the live preview mounts.
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className={styles.page} style={pageStyle}>
      <MapConfigPreview
        center={values.center}
        startZoom={values.startZoom}
        minZoom={values.minZoom}
        maxZoom={values.maxZoom}
        defaultBasemapId={values.defaultBasemapId}
        padding={cameraPadding}
        onCenterChange={setCenter}
      />

      {showOverlays && (
        <>
          <AddressSearchBar
            className={styles.addressSearch}
            placeholder={t('search.address.placeholder')}
            collapsible={isMobile}
            onSelectLocation={handleSelectLocation}
          />

          <div className={styles.settingsBar}>
            <OnboardingSettingsBar />
          </div>
        </>
      )}

      <SlidePanel
        opened
        variant="floating"
        withExpandButton={false}
        allowMinimize
        withCloseButton={false}
        onClose={isEdit ? handleBack : NOOP}
        previewMode={isMobile && previewMode}
        onPreviewModeChange={setPreviewMode}
        headerContent={
          <div className={styles.panelHeader}>
            <Text fw={FONT_WEIGHT_SEMIBOLD} size="xl">
              {isFirstRun ? t('mapSetup.firstRunTitle') : t('mapSetup.title')}
            </Text>
            {isFirstRun && (
              <Text size="sm" c="dimmed">
                {t('mapSetup.intro')}
              </Text>
            )}
          </div>
        }
        footerContent={
          <FormFooter
            leftActions={
              isEdit && (
                <Button
                  variant="subtle"
                  onClick={handleBack}
                  disabled={isSaving}
                >
                  {t('mapSetup.back')}
                </Button>
              )
            }
            rightActions={
              <Button
                onClick={handleSave}
                disabled={!canSave}
                loading={isSaving}
              >
                {t('mapSetup.save')}
              </Button>
            }
          />
        }
      >
        <MapConfigForm form={form} />
      </SlidePanel>
    </div>
  );
}
